// src/pages/Message/MarkdownContent.jsx
import { useEffect, useRef, useState } from "react";
import { marked } from "marked";
import { Modal } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import downloadIcon from "@/assets/img/icon_download.png";
import "./MarkdownTable.less"; // 引入表格样式

// --- 1. 自定义渲染器逻辑 ---
const renderer = new marked.Renderer();

/**
 * 核心修复：适配新版 marked (v11+) 的 token 模式
 * 解决原本 (header, body) 字符串参数变为 token 对象导致的 [object Object] 问题
 */
renderer.table = function(token) {
  // 1. 渲染表头：遍历 token.header 数组
  const headerHtml = token.header.map(cell => this.tablecell(cell)).join('');
  
  // 2. 渲染行内容：遍历 token.rows 嵌套数组
  const bodyHtml = token.rows.map(row => {
    const rowContent = row.map(cell => this.tablecell(cell)).join('');
    return `<tr>${rowContent}</tr>`;
  }).join('');

  // 3. 返回符合图2要求的卡片结构
  return `
    <div class="markdown-table-container">
      <div class="table-header-bar">
        <span class="table-title">数据表格</span>
        <button class="expand-table-btn" title="全屏查看">
          <svg viewBox="0 0 1024 1024" width="16" height="16">
            <path d="M160 160h256V96H96v320h64V160zm0 704V704H96v320h320v-64H160zM864 160H608V96h320v320h-64V160zm0 704v-160h64v320H608v-64h256z" fill="currentColor"></path>
          </svg>
        </button>
      </div>
      <div class="table-content-wrapper">
        <table>
          <thead><tr>${headerHtml}</tr></thead>
          <tbody>${bodyHtml}</tbody>
        </table>
      </div>
    </div>
  `;
};

/**
 * 渲染单元格：确保支持单元格内的加粗、链接等 Markdown 语法
 */
renderer.tablecell = function(token) {
  const type = token.header ? 'th' : 'td';
  const align = token.align ? ` align="${token.align}"` : '';
  // 关键：使用 parseInline 处理单元格内部的 tokens，防止丢失格式
  const content = this.parser.parseInline(token.tokens);
  return `<${type}${align}>${content}</${type}>`;
};

// 配置全局 marked 参数
marked.setOptions({
  renderer,
  breaks: true,
  gfm: true,
  sanitize: false, // 允许 HTML 容器标签通过
  silent: true,
});

// --- 2. 全局打字机状态管理 ---
let activeTypeWriterController = null;

export const stopActiveTypeWriter = () => {
  if (activeTypeWriterController && activeTypeWriterController.stop) {
    activeTypeWriterController.stop();
    activeTypeWriterController = null;
    return true;
  }
  return false;
};

const downloadMarkerHtml = `<span class="download-tag-inline" data-action="download"><img src="${downloadIcon}" class="download-icon-inline" alt="下载" /></span>`;

/**
 * 替换 @download 标记为下载图标 HTML
 */
const replaceDownloadMarkers = (text) => {
  if (!text) return text;
  return text.replace(/@download/g, downloadMarkerHtml);
};

// --- 3. 会员引导标记（@silvervip / @goldvip） ---
const VIP_GUIDE_CONFIG = {
  silver: {
    tag: "白银",
    text: "生成定制食谱需要开通会员，方可使用。",
  },
  gold: {
    tag: "黄金",
    text: "生成生酮食谱需要开通黄金会员，方可使用。",
  },
};

/**
 * 生成会员引导提示卡片 HTML
 * @param {"silver" | "gold"} level 会员级别
 */
const vipGuideHtml = (level) => {
  const config = VIP_GUIDE_CONFIG[level];
  if (!config) return "";
  // 注意：模板字符串每行必须顶格（无前导空格），否则 marked 会把行首 4 空格的
  // HTML 当成代码块解析并转义，导致卡片源码直接显示在聊天中。
  return `<div class="vip-guide-card vip-guide-${level}" data-vip-level="${level}">
<div class="vip-guide-info">
<p class="vip-guide-text">${config.text}</p>
<button type="button" class="vip-guide-btn" data-vip-level="${level}">去开通</button>

</div>

</div>`;
};

/**
 * 替换 @silvervip / @goldvip 标记为会员引导提示卡片 HTML
 */
const replaceVipMarkers = (text) => {
  if (!text) return text;
  return text
    .replace(/@silvervip/g, vipGuideHtml("silver"))
    .replace(/@goldvip/g, vipGuideHtml("gold"));
};

/**
 * 替换 @dietRecord / @sportRecord 标记为空。
 * 这类结构化记录卡片由消息下方的 React 组件（DietRecordCard / SportRecordCard）渲染，
 * 正文里的标记本身不应展示，故在此剥除（与 @diagnose / @update 同类处理）。
 */
const replaceStructuredCardMarkers = (text) => {
  if (!text) return text;
  return text
    .replace(/@dietRecord\s*/gi, "")
    .replace(/@sportRecord\s*/gi, "");
};

const MarkdownContent = ({
  content,
  message,
  handleFaqTypeWriter,
  scrollToBottom,
  handleLoading,
  onDownload,
  onVipGuide, // 会员引导回调：点击 @silvervip / @goldvip 提示卡片的"去开通"时触发，携带级别（silver / gold）
}) => {
  const contentRef = useRef(null);
  const [fullScreenHtml, setFullScreenHtml] = useState(null); // 存储放大后的表格内容

  // --- 3. 处理表格点击放大 (事件委派) ---
  useEffect(() => {
    const handleExpandClick = (e) => {
      const btn = e.target.closest('.expand-table-btn');
      if (!btn) return;

      // 阻止冒泡和默认行为，防止触发表格外部的点击/触摸逻辑
      e.preventDefault();
      e.stopPropagation();

      // 捕获最近的表格容器并提取 HTML 片段
      const container = btn.closest('.markdown-table-container');
      const tableHtml = container?.querySelector('.table-content-wrapper')?.innerHTML;
      if (tableHtml) setFullScreenHtml(tableHtml);
    };

    const handleDownloadClick = (e) => {
      const downloadEl = e.target.closest('.download-tag-inline');
      if (!downloadEl) return;

      e.preventDefault();
      e.stopPropagation();

      // 调用外部传入的下载回调，携带当前消息的 planCode
      if (onDownload) {
        onDownload(message?.planCode);
      }
    };

    const handleVipGuideClick = (e) => {
      const btn = e.target.closest('.vip-guide-btn');
      if (!btn) return;

      e.preventDefault();
      e.stopPropagation();

      // 调用外部传入的会员引导回调，携带级别（silver / gold）
      if (onVipGuide) {
        onVipGuide(btn.dataset.vipLevel);
      }
    };

    const handleTableTouch = (e) => {
      // 检查当前 touch 事件是否发生在表格容器内
      const isInsideTable = e.target.closest('.markdown-table-container');
      if (isInsideTable && (e.type === 'touchmove' || e.type === 'touchstart')) {
        // 阻止冒泡到 App.js 的全局 handleTouchMove
        e.stopPropagation();
      }
    };

    const el = contentRef.current;
    if (!el) return;

    // 监听点击（全屏按钮 + 下载图标 + 会员引导按钮）
    el.addEventListener('click', handleExpandClick);
    el.addEventListener('click', handleDownloadClick);
    el.addEventListener('click', handleVipGuideClick);
    // 监听触摸开始和触摸移动，阻止表格滚动/点击冒泡到 App.js
    el.addEventListener('touchstart', handleTableTouch, { passive: false });
    el.addEventListener('touchmove', handleTableTouch, { passive: false });

    return () => {
      el.removeEventListener('click', handleExpandClick);
      el.removeEventListener('click', handleDownloadClick);
      el.removeEventListener('click', handleVipGuideClick);
      el.removeEventListener('touchstart', handleTableTouch);
      el.removeEventListener('touchmove', handleTableTouch);
    };
  }, [content, onDownload, onVipGuide, message?.planCode]);

  // --- 4. 打字机函数核心实现 ---
  const typeWriter = (text, speed = 20) => {
    if (activeTypeWriterController && activeTypeWriterController.stop) {
      activeTypeWriterController.stop();
    }

    let index = 0;
    let timeoutId = null;
    let isActive = true;

    const completeTyping = (isNaturalEnd = true, typedContent = "") => {
      if (timeoutId) clearTimeout(timeoutId);
      isActive = false;
      if (activeTypeWriterController === controller) activeTypeWriterController = null;

      if (isNaturalEnd) {
        handleFaqTypeWriter();
      } else {
        handleFaqTypeWriter(typedContent || contentRef.current?.innerHTML || "");
      }
    };

    const controller = {
      stop: () => completeTyping(false),
      isActive: () => isActive,
    };

    activeTypeWriterController = controller;

    function type() {
      if (!isActive || !contentRef.current) return;

      if (index < text.length) {
        const nextChar = text.charAt(index);
        if (nextChar === "\n") {
          contentRef.current.innerHTML += "<br>";
        } else {
          contentRef.current.innerHTML += nextChar;
        }
        index++;
        timeoutId = setTimeout(type, speed);
        scrollToBottom();
        handleLoading(true);
      } else {
        completeTyping(true);
      }
    }

    if (contentRef.current) contentRef.current.innerHTML = "";
    type();
    return controller;
  };

  // --- 5. 渲染生命周期 ---
  useEffect(() => {
    if (!content || !contentRef.current) return;

    // 状态 1: 屏蔽
    if (message.needTypeWriter === 1) return;

    // 状态 3: 触发 FAQ 打字机
    if (message.needTypeWriter === 3) {
      // 先剥除结构化记录标记（@dietRecord/@sportRecord），避免打字机把协议码打出来；
      // 卡片由消息下方 React 组件（DietRecordCard/SportRecordCard）渲染
      const vipMarkedContent = replaceVipMarkers(replaceDownloadMarkers(replaceStructuredCardMarkers(content)));
      // 当内容里包含 vip-guide 卡片 HTML 时，typeWriter 逐字符输出会把标签当文本，
      // 因此遇到 vip-guide 相关内容时退化为一次性渲染
      if (vipMarkedContent.includes("vip-guide-card")) {
        contentRef.current.innerHTML = marked(vipMarkedContent);
        handleFaqTypeWriter();
        return;
      }
      typeWriter(vipMarkedContent);
      return;
    }

    // 状态 2: 普通流式或静态渲染
    try {
      let _content = content;
      const trailingEllipsisRegex = /\.{3}$/;
      const cleanContent = _content.replace(trailingEllipsisRegex, "");

// 自动修复 Markdown 表格
//       const fixedMarkdown = fixMarkdownTable(cleanContent);

// 流式输出时手动添加省略号反馈
//       const finalMarkdown = message.loading
//           ? fixedMarkdown + "..."
//           : fixedMarkdown;
      const finalMarkdown = message?.loading ? cleanContent + "..." : cleanContent;
      // 替换 @download / @silvervip / @goldvip 标记
      const processedContent = replaceVipMarkers(replaceDownloadMarkers(replaceStructuredCardMarkers(finalMarkdown)));
      // 执行渲染并插入 DOM
      contentRef.current.innerHTML = marked(processedContent);
    } catch (error) {
      console.error("Markdown 解析错误:", error);
      contentRef.current.innerHTML = content;
    }

    return () => {
      if (activeTypeWriterController) activeTypeWriterController.stop();
    };
  }, [content, message]);

  if (!content) return null;

  return (
    <>
      <div ref={contentRef} className="markdown-body-custom" />

      {/* --- 6. 全屏模态框 (对应图3) --- */}
      <Modal
        open={!!fullScreenHtml}
        footer={null}
        closable={false}
        width="100%"
        className="table-fullscreen-modal"
        destroyOnHidden // 修复 deprecated 警告
        onCancel={() => setFullScreenHtml(null)}
      >
        <div className="fullscreen-header">
          <ArrowLeftOutlined className="back-icon" onClick={() => setFullScreenHtml(null)} />
          <span>表格详情</span>
        </div>
        <div 
          className="fullscreen-table-content" 
          dangerouslySetInnerHTML={{ __html: fullScreenHtml }} 
          onTouchStart={(e) => e.stopPropagation()} 
          onTouchMove={(e) => e.stopPropagation()}
        />
      </Modal>
    </>
  );
};

export default MarkdownContent;